import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './ImageViewerModal.module.css';

export interface ImageViewerModalProps {
    isOpen: boolean;
    imageUrl: string;
    imageAlt: string;
    caption?: string;
    onClose: () => void;
}

const ImageViewerModal: React.FC<ImageViewerModalProps> = ({
    isOpen,
    imageUrl,
    imageAlt,
    caption,
    onClose
}) => {
    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className={styles.overlay}
                    onClick={handleOverlayClick}
                    role="presentation"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <motion.div
                        className={styles.container}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Image viewer"
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <button
                            className={styles.closeButton}
                            onClick={onClose}
                            aria-label="Close image viewer"
                            type="button"
                        >
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <div className={styles.imageWrapper}>
                            <img
                                src={imageUrl}
                                alt={imageAlt}
                                className={styles.image}
                            />
                        </div>

                        {caption && (
                            <div className={styles.captionContainer}>
                                <p className={styles.caption}>{caption}</p>
                            </div>
                        )}

                        {imageAlt && !caption && (
                            <div className={styles.captionContainer}>
                                <p className={styles.caption}>{imageAlt}</p>
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default ImageViewerModal;
